import React from 'react';
import WorldMap from '../../components/WorldMap';
import ContextMenu from '../../components/ContextMenu';
import { type LiveVehicleTrack } from '../../components/WorldMap';
import {
  getInterventionDisplayLines,
  getInterventionPhaseUiKey,
  type InstanceRow,
} from '../../components/interventions/instanceModel';
import { formatDistanceKm } from '../mapUtils';
import { Batiment, Vehicule } from '../../../../shared/types/game';
import { type ContextMenuState } from '../hooks/useMapData';

interface DispatchViewProps {
  activeInterventions: InstanceRow[];
  selectedIntervention: InstanceRow | null;
  selectedDisplay: { headline: string; subline: string } | null;
  setSelectedInterventionId: (id: number) => void;
  getInterventionPriority: (inv: InstanceRow) => number;
  setInterventionPriority: (inv: InstanceRow, priority: number) => void;
  setMainView: (view: 'dispatch' | 'resources' | 'buildings' | 'interventions') => void;
  buildings: Batiment[];
  interventions: InstanceRow[];
  liveVehicleTracks: LiveVehicleTrack[];
  showInterventionSectors: boolean;
  contextMenu: ContextMenuState;
  setContextMenu: (state: ContextMenuState) => void;
  handleBuildingPlace: (lat: number, lng: number, buildingType: string, buildingName: string) => void;
  availableVehicles: number;
  availablePersonnel: number;
  availabilityPercent: number;
  availabilityCircumference: number;
  availabilityOffset: number;
  handleFutureFeature: (label: string) => void;
  availableUnitsRows: {
    id: number;
    unit: string;
    type: string;
    service: string;
    crew: string;
    state: string;
    eta: string;
  }[];
  buildingFilterOptions: { id: number; name: string }[];
  vehicleTypeFilterOptions: string[];
  playerVehicles: Vehicule[];
  unitsSearch: string;
  setUnitsSearch: (v: string) => void;
  buildingFilter: number | 'all';
  setBuildingFilter: (v: number | 'all') => void;
  vehicleTypeFilter: string;
  setVehicleTypeFilter: (v: string) => void;
  unitsFilter: 'all' | 'disponible' | 'en_mission' | 'en_maintenance';
  setUnitsFilter: (v: 'all' | 'disponible' | 'en_mission' | 'en_maintenance') => void;
  selectedInterventionVehicleRows: Vehicule[];
}

const DispatchView: React.FC<DispatchViewProps> = ({
  activeInterventions,
  selectedIntervention,
  selectedDisplay,
  setSelectedInterventionId,
  getInterventionPriority,
  setInterventionPriority,
  setMainView,
  buildings,
  interventions,
  liveVehicleTracks,
  showInterventionSectors,
  contextMenu,
  setContextMenu,
  handleBuildingPlace,
  availableVehicles,
  availablePersonnel,
  availabilityPercent,
  availabilityCircumference,
  availabilityOffset,
  handleFutureFeature,
  availableUnitsRows,
  buildingFilterOptions,
  vehicleTypeFilterOptions,
  playerVehicles,
  unitsSearch,
  setUnitsSearch,
  buildingFilter,
  setBuildingFilter,
  vehicleTypeFilter,
  setVehicleTypeFilter,
  unitsFilter,
  setUnitsFilter,
  selectedInterventionVehicleRows,
}) => (
  <div className="grid h-full grid-cols-[300px,minmax(0,1fr),420px] gap-3">
    <aside className="flex min-h-0 flex-col gap-3">
      <section className="rounded-lg border border-slate-700/75 bg-slate-900/90 shadow-xl shadow-black/25">
        <div className="border-b border-slate-700/70 px-3 py-2">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-red-300">
            Interventions en cours ({activeInterventions.length})
          </h2>
        </div>
        <div className="max-h-[42vh] space-y-2 overflow-y-auto p-2">
          {activeInterventions.slice(0, 7).map((inv, idx) => {
            const lines = getInterventionDisplayLines(inv, 'Intervention');
            const priority = getInterventionPriority(inv);
            return (
              <button
                key={inv.id}
                type="button"
                onClick={() => setSelectedInterventionId(inv.id)}
                className={`w-full rounded-md border p-2 text-left transition-colors ${
                  selectedIntervention?.id === inv.id
                    ? 'border-red-500/70 bg-red-950/35'
                    : 'border-slate-700/80 bg-slate-950/70 hover:bg-slate-800/80'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-xs font-bold uppercase text-slate-100">{lines.headline}</p>
                    <p className="mt-0.5 line-clamp-1 text-[10px] text-slate-400">{lines.subline || 'Adresse non précisée'}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="rounded border border-red-500/45 bg-red-500/20 px-1.5 py-0.5 text-[9px] font-semibold text-red-200">
                      Priorité
                    </span>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={priority}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setInterventionPriority(inv, Number(e.target.value))}
                      className="w-14 rounded border border-red-500/45 bg-slate-900/95 px-1.5 py-0.5 text-[10px] font-semibold text-red-200 outline-none focus:border-red-400"
                      title="Priorité modifiable par le joueur"
                    />
                  </div>
                </div>
                <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-400">
                  <span>#{inv.numero_intervention || inv.id}</span>
                  <span>{formatDistanceKm(45.764, 4.8357, Number(inv.latitude || 45.764), Number(inv.longitude || 4.8357))}</span>
                  <span>{2 + idx} min</span>
                </div>
              </button>
            );
          })}
          {activeInterventions.length === 0 ? (
            <p className="rounded-md border border-slate-700/80 bg-slate-950/70 px-2 py-6 text-center text-xs text-slate-500">
              Aucune intervention active
            </p>
          ) : null}
        </div>
      </section>

      <section className="min-h-0 flex-1 rounded-lg border border-slate-700/75 bg-slate-900/90 shadow-xl shadow-black/25">
        <div className="border-b border-slate-700/70 px-3 py-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-200">
            {selectedDisplay?.headline || 'Intervention'}
          </h3>
        </div>
        <div className="space-y-3 p-3 text-sm">
          <div className="grid grid-cols-3 gap-1 rounded-md border border-slate-700/75 bg-slate-950/80 p-1 text-[10px] uppercase tracking-wide">
            <span className="rounded bg-slate-800 px-2 py-1 text-center text-slate-200">Détails</span>
            <span className="rounded px-2 py-1 text-center text-slate-500">Notes</span>
            <span className="rounded px-2 py-1 text-center text-slate-500">Historique</span>
          </div>
          <div className="rounded-md border border-slate-700/80 bg-slate-950/70 p-2.5 text-[11px]">
            <p className="text-slate-500">Adresse</p>
            <p className="mt-0.5 text-slate-100">{selectedDisplay?.subline || 'Non renseignée'}</p>
            <p className="mt-2 text-slate-500">Type</p>
            <p className="mt-0.5 text-slate-100">{selectedDisplay?.headline || 'Intervention'}</p>
            <p className="mt-2 text-slate-500">Appel reçu</p>
            <p className="mt-0.5 text-slate-100">
              {selectedIntervention?.date_creation
                ? new Date(selectedIntervention.date_creation).toLocaleString('fr-FR')
                : '—'}
            </p>
            <p className="mt-2 text-slate-500">Météo locale</p>
            <p className="mt-0.5 text-slate-100">En cours de mise à jour, disponible prochainement.</p>
          </div>
          <button
            type="button"
            onClick={() => setMainView('interventions')}
            className="w-full rounded-md border border-sky-500/55 bg-sky-500/15 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-sky-100 hover:bg-sky-500/25"
          >
            Plus d'infos
          </button>
        </div>
      </section>
    </aside>

    <main className="grid min-h-0 grid-rows-[minmax(0,1fr),280px] gap-3">
      <section className="relative min-h-0 overflow-hidden rounded-lg border border-slate-700/75 bg-slate-900 shadow-xl shadow-black/30">
        <WorldMap
          buildings={buildings}
          showInterventionSectors={showInterventionSectors}
          interventions={interventions}
          liveVehicles={liveVehicleTracks}
        />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-slate-950/55 to-transparent" />
        <div className="absolute bottom-3 right-3 z-[500] rounded-lg border border-slate-600/70 bg-slate-950/90 px-3 py-2 text-[10px] text-slate-200 shadow-lg shadow-black/35">
          <p className="mb-1 font-semibold uppercase tracking-[0.12em] text-slate-300">Légende véhicules live</p>
          <div className="space-y-1">
            <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full border border-rose-100 bg-rose-500" />En route / mission</div>
            <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full border border-amber-100 bg-amber-500" />Retour hors secteur</div>
            <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full border border-emerald-100 bg-emerald-500" />Disponible radio</div>
            <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full border border-sky-100 bg-sky-500" />Transport / arrivée hôpital</div>
            <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full border border-yellow-100 bg-yellow-500" />Préalerte</div>
          </div>
        </div>

        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            onClose={() => setContextMenu(null)}
            onBuildClick={(buildingType, buildingName) =>
              handleBuildingPlace(contextMenu.lat, contextMenu.lng, buildingType, buildingName)
            }
          />
        )}
      </section>

      <section className="grid grid-cols-[1.25fr,1fr,1fr] gap-3 text-[11px]">
        <div className="rounded-lg border border-slate-700/85 bg-slate-900 p-3.5 shadow-xl shadow-black/35">
          <p className="text-[10px] uppercase tracking-[0.12em] text-red-300">Alertes et notifications</p>
          <div className="mt-2 space-y-1.5 text-slate-300">
            <p className="rounded border border-red-500/30 bg-red-950/40 px-2 py-1">Zone bloquée · Avenue des Frères Lumière</p>
            <p className="rounded border border-amber-500/30 bg-amber-950/30 px-2 py-1">Module météo en cours de mise à jour, disponible prochainement.</p>
            <p className="rounded border border-sky-500/30 bg-sky-950/30 px-2 py-1">
              Demande d'assistance SAMU · {Math.max(0, activeInterventions.length - 1)} en attente
            </p>
          </div>
          <button
            type="button"
            onClick={() => setMainView('interventions')}
            className="mt-2 w-full rounded border border-slate-600/70 bg-slate-800/80 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-200"
          >
            Voir toutes les alertes
          </button>
        </div>
        <div className="rounded-lg border border-slate-700/85 bg-slate-900 p-3.5 shadow-xl shadow-black/35">
          <p className="text-[10px] uppercase tracking-[0.12em] text-emerald-300">Ressources globales</p>
          <div className="mt-2 space-y-2 text-slate-300">
            <div className="rounded border border-slate-700/70 bg-slate-950/80 px-2.5 py-1.5">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Flotte véhicules</p>
              <p className="mt-0.5 text-sm font-semibold text-slate-100">
                {availableVehicles} <span className="text-xs font-normal text-emerald-300">dispo.</span>
              </p>
            </div>
            <div className="rounded border border-slate-700/70 bg-slate-950/80 px-2.5 py-1.5">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Effectif personnel</p>
              <p className="mt-0.5 text-sm font-semibold text-slate-100">
                {availablePersonnel} <span className="text-xs font-normal text-emerald-300">actifs</span>
              </p>
            </div>
          </div>
          <div className="mt-2.5 rounded border border-slate-700/70 bg-slate-950/75 p-2">
            <div className="mb-1 flex items-center justify-between text-[10px] text-slate-500">
              <span>Taux de disponibilité</span>
              <span>{availabilityPercent}%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative h-16 w-16 shrink-0">
                <svg viewBox="0 0 80 80" className="h-16 w-16 -rotate-90">
                  <circle cx="40" cy="40" r="30" stroke="rgba(51,65,85,0.9)" strokeWidth="8" fill="none" />
                  <circle
                    cx="40"
                    cy="40"
                    r="30"
                    stroke="rgba(16,185,129,0.9)"
                    strokeWidth="8"
                    strokeLinecap="round"
                    fill="none"
                    strokeDasharray={availabilityCircumference}
                    strokeDashoffset={availabilityOffset}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-emerald-300">
                  {availabilityPercent}%
                </div>
              </div>
              <div className="min-w-0 text-[10px] text-slate-400">
                <p>Disponibilité opérationnelle</p>
                <p className="mt-0.5 text-emerald-300">Capacité de réponse conforme</p>
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-slate-700/85 bg-slate-900 p-3.5 shadow-xl shadow-black/35">
          <p className="text-[10px] uppercase tracking-[0.12em] text-sky-300">Alliances actives</p>
          <div className="mt-2 rounded border border-slate-700/70 bg-slate-950/75 px-2 py-2 text-slate-300">
            En cours de mise à jour, disponible prochainement.
          </div>
          <button
            type="button"
            onClick={() => handleFutureFeature('Alliances')}
            className="mt-2 w-full rounded border border-slate-700/70 bg-slate-800/50 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400"
          >
            Voir toutes les alliances
          </button>
        </div>
      </section>
    </main>

    <aside className="flex min-h-0 flex-col gap-3">
      <section className="rounded-lg border border-slate-700/75 bg-slate-900/90 shadow-xl shadow-black/25">
        <div className="border-b border-slate-700/70 px-3 py-2">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-100">Engager des ressources</h2>
        </div>
        <div className="space-y-3 p-3">
          <div className="grid grid-cols-3 gap-2 text-center text-[11px]">
            <div className="rounded-md border border-red-500/35 bg-red-600/10 px-2 py-2">
              <p className="font-semibold text-red-200">FPT</p>
              <p className="mt-1 text-red-300">Prêt</p>
            </div>
            <div className="rounded-md border border-orange-500/35 bg-orange-600/10 px-2 py-2">
              <p className="font-semibold text-orange-200">VSAV</p>
              <p className="mt-1 text-orange-300">Prêt</p>
            </div>
            <div className="rounded-md border border-sky-500/35 bg-sky-600/10 px-2 py-2">
              <p className="font-semibold text-sky-200">EPA</p>
              <p className="mt-1 text-sky-300">Prêt</p>
            </div>
          </div>

          <div className="rounded-md border border-slate-700/80 bg-slate-950/70 p-2.5">
            <div className="mb-2 flex items-center justify-between text-[11px]">
              <span className="uppercase tracking-wide text-slate-400">Unités disponibles ({availableUnitsRows.length})</span>
              <span className="font-semibold text-emerald-300">
                {new Set(playerVehicles.map((v) => Number(v.batiment_id || 0)).filter((id) => id > 0)).size || 0} bases
              </span>
            </div>
            <div className="mb-2 grid grid-cols-2 gap-1.5">
              <input
                type="search"
                value={unitsSearch}
                onChange={(e) => setUnitsSearch(e.target.value)}
                placeholder="Rechercher une unité..."
                className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1 text-[11px] text-slate-100 placeholder:text-slate-500 outline-none focus:border-emerald-500/60"
              />
              <select
                value={buildingFilter}
                onChange={(e) => {
                  const v = e.target.value;
                  setBuildingFilter(v === 'all' ? 'all' : Number(v));
                }}
                className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1 text-[11px] text-slate-100 outline-none focus:border-emerald-500/60"
              >
                <option value="all">Tous les bâtiments</option>
                {buildingFilterOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
              <select
                value={vehicleTypeFilter}
                onChange={(e) => setVehicleTypeFilter(e.target.value)}
                className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1 text-[11px] text-slate-100 outline-none focus:border-emerald-500/60"
              >
                <option value="all">Tous les types</option>
                {vehicleTypeFilterOptions.map((typeName) => (
                  <option key={typeName} value={typeName}>
                    {typeName}
                  </option>
                ))}
              </select>
              <select
                value={unitsFilter}
                onChange={(e) => setUnitsFilter(e.target.value as 'all' | 'disponible' | 'en_mission' | 'en_maintenance')}
                className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1 text-[11px] text-slate-100 outline-none focus:border-emerald-500/60"
              >
                <option value="all">Statut: tous</option>
                <option value="disponible">Prêts</option>
                <option value="en_mission">Mission</option>
                <option value="en_maintenance">Maintenance</option>
              </select>
            </div>
            <div className="overflow-hidden rounded-md border border-slate-700/70 bg-slate-900/90">
              <div className="grid grid-cols-[minmax(0,1.3fr),minmax(0,1fr),minmax(0,0.9fr),56px,minmax(0,1fr),48px] border-b border-slate-700/70 bg-slate-950/85 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400">
                <span>Unité</span>
                <span>Type</span>
                <span>Service</span>
                <span className="text-center">Eq.</span>
                <span>État</span>
                <span className="text-right">Disp.</span>
              </div>
              <div className="max-h-44 overflow-y-auto text-[11px]">
                {availableUnitsRows.map((row) => (
                  <div
                    key={row.id}
                    className="grid grid-cols-[minmax(0,1.3fr),minmax(0,1fr),minmax(0,0.9fr),56px,minmax(0,1fr),48px] items-center gap-1 border-b border-slate-800/65 px-2 py-1.5 last:border-b-0"
                  >
                    <span className="truncate pr-1 text-slate-200">{row.unit}</span>
                    <span className="truncate text-slate-400">{row.type}</span>
                    <span className="truncate text-slate-300">{row.service}</span>
                    <span className="text-center font-semibold text-slate-200">{row.crew}</span>
                    <span className={row.state === 'PRÊT' ? 'text-emerald-300' : 'text-amber-300'}>{row.state}</span>
                    <span className="text-right text-slate-300">{row.eta}</span>
                  </div>
                ))}
                {availableUnitsRows.length === 0 ? (
                  <p className="px-2 py-3 text-center text-slate-500">Aucune unité correspondant au filtre</p>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="min-h-0 flex-1 rounded-lg border border-slate-700/75 bg-slate-900/90 shadow-xl shadow-black/25">
        <div className="border-b border-slate-700/70 px-3 py-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-100">Exigences de l'intervention</h3>
        </div>
        <div className="space-y-3 p-3 text-xs">
          <div className="rounded-md border border-slate-700/80 bg-slate-950/70 p-2.5">
            <p className="text-slate-500">Niveau de risque</p>
            <p className="mt-1 font-semibold text-red-300">
              {selectedIntervention ? (getInterventionPhaseUiKey(selectedIntervention) === 'wait' ? 'Très élevé' : 'Élevé') : '—'}
            </p>
          </div>
          <div className="rounded-md border border-slate-700/80 bg-slate-950/70 p-2.5">
            <p className="text-slate-500">Personnel disponible</p>
            <p className="mt-1 font-semibold text-emerald-300">{availablePersonnel.toLocaleString('fr-FR')} agents</p>
          </div>
          <div className="rounded-md border border-slate-700/80 bg-slate-950/70 p-2.5">
            <p className="text-slate-500">Unités de l'intervention</p>
            <div className="mt-1.5 space-y-1">
              {selectedInterventionVehicleRows.length > 0 ? (
                selectedInterventionVehicleRows.slice(0, 4).map((v) => (
                  <div key={v.id} className="flex items-center justify-between rounded border border-slate-700/70 bg-slate-900/80 px-2 py-1">
                    <span className="truncate pr-2 text-slate-200">{v.nom}</span>
                    <span className="text-[10px] text-emerald-300">ENGAGÉE</span>
                  </div>
                ))
              ) : (
                <p className="text-[11px] text-slate-500">Aucune unité encore engagée.</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setMainView('interventions')}
            className="w-full rounded-md border border-emerald-500/60 bg-emerald-600/85 px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-white hover:bg-emerald-500"
          >
            Engager les ressources
          </button>
        </div>
      </section>
    </aside>
  </div>
);

export default DispatchView;
